import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, Calendar, Bell, Shield, Stethoscope, Sparkles, Heart, Brain, Baby, Bone, Eye, Smile } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const specialties = [
  { name: "Cardiology", Icon: Heart },
  { name: "Dermatology", Icon: Sparkles },
  { name: "Pediatrics", Icon: Baby },
  { name: "Neurology", Icon: Brain },
  { name: "Orthopedics", Icon: Bone },
  { name: "General Physician", Icon: Stethoscope },
  { name: "Ophthalmology", Icon: Eye },
  { name: "Dentistry", Icon: Smile },
];

const steps = [
  { icon: Search, title: "Find the right doctor", desc: "Search by specialty, location and availability." },
  { icon: Calendar, title: "Book in seconds", desc: "Pick a time slot that works — instant confirmation." },
  { icon: Bell, title: "Manage effortlessly", desc: "Reschedule or cancel any time. Get reminders." },
];

const faqs = [
  { q: "Is DocCare free to use for patients?", a: "Yes. Patients can search, book and manage appointments at no cost. Consultation fees are set by each clinic." },
  { q: "How is my data protected?", a: "DocCare uses role-based access, encrypted sessions and never exposes credentials to the browser. Data is stored in a private database." },
  { q: "Can doctors join DocCare?", a: "Doctors are onboarded by admins. Once your account is active, you can manage availability and appointments from the doctor dashboard." },
  { q: "Does DocCare offer video consultations?", a: "Some doctors offer video consultations. Look for the video badge on their profile." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
            <span className="text-xl font-bold text-slate-900">DocCare</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#specialties" className="hover:text-slate-900">Specialties</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-signin">Sign in</Button></Link>
            <Link to="/doctors"><Button className="rounded-full" data-testid="nav-find-doctor">Find a Doctor</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pt-12 lg:pt-20 pb-12 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-medium mb-5">
            <Sparkles className="w-3 h-3" /> Demo product — realistic data
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            Healthcare appointments,<br />
            <span className="brand-gradient-text">made simple.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl">
            Find the right doctor, check availability and manage your appointments from one calm, simple platform.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/doctors"><Button size="lg" className="rounded-full px-7" data-testid="hero-find">Find a Doctor</Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-signin">Sign In</Button></Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> Role-based access</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-600" /> No double-booking</span>
          </div>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="Doctor with patient" className="rounded-3xl object-cover w-full aspect-[4/5] lg:aspect-[4/5] max-h-[560px] shadow-lg" />
          <div className="absolute -bottom-4 -left-4 hidden md:block bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
            <p className="text-xs text-slate-500">Next appointment</p>
            <p className="font-semibold text-slate-900">Dr. Emily Chen · Tomorrow 10:00</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-bold text-slate-900">How DocCare works</h2>
          <p className="mt-3 text-slate-600">A three-step flow designed to remove friction from healthcare bookings.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.title} className="doccare-card p-6 hover-lift">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Specialties */}
      <section id="specialties" className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-slate-900">Browse by specialty</h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {specialties.map((s) => (
            <Link key={s.name} to={`/doctors?specialty=${encodeURIComponent(s.name)}`} className="doccare-card p-5 hover-lift flex items-center gap-3" data-testid={`specialty-${s.name}`}>
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center">
                <s.Icon className="w-5 h-5" />
              </div>
              <span className="font-medium text-slate-900">{s.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Role sections */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 grid md:grid-cols-3 gap-6">
        {[
          { title: "For Patients", body: "Discover doctors, book appointments and manage everything from one place." },
          { title: "For Doctors", body: "Manage today's schedule, set availability and view patient bookings." },
          { title: "For Admins", body: "Onboard doctors, track platform-wide activity and manage specialties." },
        ].map((c) => (
          <div key={c.title} className="doccare-card p-6">
            <h3 className="font-semibold text-slate-900">{c.title}</h3>
            <p className="text-sm text-slate-600 mt-2">{c.body}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-slate-900">Frequently asked</h2>
        <Accordion type="single" collapsible className="mt-6">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
              <AccordionTrigger className="text-left" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="rounded-3xl bg-slate-900 text-white p-10 lg:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold">Ready to book your next visit?</h2>
            <p className="text-slate-300 mt-2">Create a free patient account in under a minute.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/register"><Button size="lg" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="cta-register">Create account</Button></Link>
            <Link to="/doctors"><Button size="lg" variant="outline" className="rounded-full bg-transparent border-white text-white hover:bg-white hover:text-slate-900" data-testid="cta-browse">Browse doctors</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 text-sm text-slate-500 flex flex-col md:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} DocCare — demo product</p>
          <p>Healthcare appointments, made simple.</p>
        </div>
      </footer>
    </div>
  );
}
